import { useEffect, useState, useCallback } from "react";
import {
  Loader2, FileText, Download, FileArchive, Eye, Send, CheckCircle2, AlertTriangle,
  Settings2, Save, ShieldAlert, ListChecks, FileSpreadsheet, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExportExcelButton } from "@/components/common/ExportExcelButton";

const GEN_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld"];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
// Feb-2026 · Msg 245 — Limpieza del modal de catálogos SITI:
// - Se quitaron los bloques de mapeo manual (Ciudades, Actividades, Monedas): las columnas
//   26 y 28 se resuelven SIEMPRE con la clave oficial del expediente del cliente
//   (`localidad_clave`, `actividad_economica_clave`). La lógica de respaldo del generador
//   (`pld_reportes.py::_datos_persona`) queda intacta por si una entidad legítima llega a
//   necesitarla, pero la captura manual desde esta pantalla ya no procede.
// - Se quitó `tipo_operacion_default` porque el generador deduce el tipo de operación
//   caso-por-caso desde el evento real (`pld_reportes.py::_deducir_tipo_op_*`).
// Quedan cuatro campos únicamente:
const CAMPOS_CLAVE = [
  { k: "clave_sujeto_obligado", label: "Clave del sujeto obligado (CASFIM · inicia con 069)" },
  { k: "localidad_default",     label: "Localidad de sucursal (8 dígitos)" },
  { k: "cp_sucursal_default",   label: "Código postal de sucursal (5 dígitos)" },
  { k: "moneda_default",        label: "Moneda por defecto" },
];

async function downloadBlob(url, filename) {
  const token = localStorage.getItem("cos_token");
  const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("No se pudo descargar el archivo");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
}

export default function ReportesUif() {
  const { user } = useAuth();
  const role = user?.role;
  const canView = GEN_ROLES.includes(role);
  const canEditCat = role === "oficial_cumplimiento";
  const canGenerar = GEN_ROLES.includes(role);
  const canAcuse = role === "oficial_cumplimiento";

  const [cat, setCat] = useState({ editables: {}, mapeos: {}, fijos: {} });
  const [reportes, setReportes] = useState([]);
  const [casosListos, setCasosListos] = useState([]);
  const [busy, setBusy] = useState(false);

  const now = new Date();
  const [anio, setAnio] = useState(String(now.getFullYear()));
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [validacion, setValidacion] = useState(null);

  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({});

  const [prev, setPrev] = useState(null);
  // Feb-2026 · Iter-107: aviso persistente cuando el reporte se generó con órgano supervisor
  // marcador (XXXXXX). El backend devuelve `aviso_organo` en la respuesta; el OC debe verlo
  // hasta que decida cerrar el banner (no un toast que desaparece solo).
  const [avisoOrgano, setAvisoOrgano] = useState(null);
  const _capturarAvisoOrgano = (data) => {
    if (data?.aviso_organo) {
      setAvisoOrgano({
        ...data.aviso_organo,
        archivo_nombre: data?.reporte?.archivo_nombre,
        reporte_id: data?.reporte?.id,
        generado_en: new Date().toISOString(),
      });
    } else {
      // Si el reporte más reciente ya no tiene aviso, limpiamos.
      setAvisoOrgano(null);
    }
  };
  // Un reporte del histórico es BORRADOR NO ENVIABLE si:
  //   (a) su nombre termina en `.XXXXXX` (marcador del órgano supervisor sin capturar), o
  //   (b) el backend lo marcó con `borrador_no_enviable=true` porque uno o más clientes
  //       tenían columnas SITI incompletas (localidad y/o actividad económica sin clave).
  const esBorradorNoEnviable = (row) =>
    String(row?.archivo_nombre || "").endsWith(".XXXXXX") || Boolean(row?.borrador_no_enviable);
  const motivoBorrador = (row) => {
    if (row?.borrador_no_enviable) {
      const nombres = (row.clientes_incompletos || []).map((c) => c.nombre || "(sin nombre)").join(", ");
      return (
        "No se puede transmitir: BORRADOR NO ENVIABLE. Uno o más clientes tienen columnas SITI "
        + "incompletas (localidad col 26 y/o actividad económica col 28). "
        + (nombres ? `Clientes afectados: ${nombres}. ` : "")
        + "Corrige los expedientes y regenera el reporte."
      );
    }
    return (
      "No se puede transmitir: este archivo es un BORRADOR DE REVISIÓN. El órgano supervisor "
      + "(columna 4 del layout) está en marcador (XXXXXX) y el SITI lo rechazaría. Pide al "
      + "super_admin que capture la clave CNBV definitiva y regenera el reporte."
    );
  };
  // Feb-2026 · Mejora Histórico SITI: formato legible de fechas ISO y renderizado del
  // estado del plazo regulatorio (calculado en el backend según reglas DOF: 28ª relevantes,
  // 29ª/34ª inusuales/IP y 33ª 24h). El color del badge sirve como alerta visual para el OC.
  const fmtFechaHora = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
    } catch { return "—"; }
  };
  const fmtFecha = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return "—"; }
  };
  const PLAZO_CFG = {
    enviado:    { label: "Enviado",    cls: "border-slate-300 bg-slate-100 text-slate-700" },
    vencido:    { label: "Vencido",    cls: "border-red-400 bg-red-100 text-red-800" },
    urgente:    { label: "Urgente",    cls: "border-orange-400 bg-orange-100 text-orange-800" },
    por_vencer: { label: "Por vencer", cls: "border-amber-400 bg-amber-100 text-amber-800" },
    en_tiempo:  { label: "En tiempo",  cls: "border-emerald-400 bg-emerald-100 text-emerald-800" },
  };
  const renderPlazo = (row) => {
    const fecha = row?.fecha_limite_calc;
    const estado = row?.estado_plazo || "";
    if (!fecha) return <span className="text-xs text-slate-400">—</span>;
    const cfg = PLAZO_CFG[estado] || PLAZO_CFG.en_tiempo;
    const dias = row?.dias_restantes;
    let leyenda = cfg.label;
    if (estado === "vencido" && Number.isFinite(dias)) leyenda = `Vencido hace ${Math.abs(dias)} día${Math.abs(dias) === 1 ? "" : "s"}`;
    else if (estado === "enviado") leyenda = "Enviado";
    else if (Number.isFinite(dias)) leyenda = dias === 0 ? "Vence hoy" : `${dias} día${dias === 1 ? "" : "s"} restantes`;
    return (
      <span className="flex flex-col gap-0.5" data-testid={`plazo-${row.id}`}>
        <span className="text-xs font-medium text-slate-700">{fmtFecha(fecha)}</span>
        <Badge className={`${cfg.cls} border`} data-testid={`plazo-badge-${row.id}`}>
          {leyenda}
        </Badge>
      </span>
    );
  };
  // Tarea 6 (jul-2026): cuando el backend bloquea con HTTP 400 y payload
  // `{mensaje, faltantes_config, faltantes_clientes}`, mostramos un diálogo detallado
  // en lugar del toast genérico, para que el OC pueda leer QUÉ falta y en QUÉ cliente.
  const [bloqueo, setBloqueo] = useState(null);
  const [acuseFor, setAcuseFor] = useState(null);
  const [acuseForm, setAcuseForm] = useState({ acuse: "", folio_siti: "", fecha_envio: "" });

  const loadCat = useCallback(async () => {
    const { data } = await api.get("/pld/reportes/catalogos");
    setCat(data);
    setCatForm({ ...data.editables });
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([
        api.get("/pld/reportes").then((x) => x.data),
        api.get("/pld/oc/bandeja").then((x) => x.data).catch(() => ({})),
      ]);
      setReportes(r.reportes || []);
      setCasosListos(b.casos_listos_para_reporte || []);
      await loadCat();
    } catch (e) { console.debug("Reportes UIF no disponibles (sin acceso):", e?.response?.status); }
  }, [loadCat]);
  useEffect(() => { if (canView) loadAll(); }, [canView, loadAll]);

  const validar = async (extra = {}) => {
    setBusy(true);
    try {
      const params = { tipo: "relevante", anio, mes, ...extra };
      const { data } = await api.get("/pld/reportes/validar", { params });
      setValidacion(data);
      if (data.listo) toast.success("Validación correcta: catálogos completos.");
      else toast.warning(`Faltan ${data.faltantes_catalogo.length + data.faltantes_config.length} código(s) de catálogo.`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const guardarCatalogos = async () => {
    // Feb-2026 · Iter-106 Tarea 1: validación CASFIM para SOFOM ENR.
    // Permitir vacío (CONDUSEF tarda en asignar la clave; la entidad no puede detenerse).
    // Si viene: numérico, máx 7 chars, empezar con 069.
    const cso = String(catForm.clave_sujeto_obligado || "").trim();
    if (cso) {
      if (!/^\d+$/.test(cso)) {
        toast.error("La Clave del sujeto obligado debe ser únicamente numérica. Se toma del catálogo CASFIM, sin guion y con cero al inicio.");
        return;
      }
      if (cso.length > 7) {
        toast.error("La Clave del sujeto obligado tiene un máximo de 7 caracteres numéricos (catálogo CASFIM).");
        return;
      }
      if (!cso.startsWith("069")) {
        toast.error("La Clave del sujeto obligado debe iniciar con 069, sector CASFIM de SOFOM ENR (sin guion, con cero al inicio).");
        return;
      }
    }
    // Feb-2026 · Msg 245: Localidad de sucursal = 8 dígitos numéricos (catálogo CNBV LOCALIDAD).
    const loc = String(catForm.localidad_default || "").trim();
    if (loc && !/^\d{8}$/.test(loc)) {
      toast.error("La Localidad de sucursal debe ser exactamente 8 dígitos numéricos (catálogo CNBV LOCALIDAD RIPS F36).");
      return;
    }
    // Código postal de sucursal = 5 dígitos.
    const cp = String(catForm.cp_sucursal_default || "").trim();
    if (cp && !/^\d{5}$/.test(cp)) {
      toast.error("El Código postal de sucursal debe ser exactamente 5 dígitos numéricos.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/pld/reportes/catalogos", { ...catForm });
      toast.success("Catálogos SITI actualizados.");
      setCatOpen(false);
      await loadCat();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // Handler común para errores HTTP 400 con payload estructurado (Tarea 6).
  const _manejarError = (e) => {
    const det = e?.response?.data?.detail;
    if (det && typeof det === "object" && (det.faltantes_config || det.faltantes_clientes)) {
      setBloqueo({
        mensaje: det.mensaje || "No se puede generar el reporte.",
        faltantes_config: det.faltantes_config || [],
        faltantes_clientes: det.faltantes_clientes || [],
      });
      return;
    }
    toast.error(apiError(e));
  };

  const generarRelevante = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pld/reportes/relevante", { anio: Number(anio), mes: Number(mes) });
      toast.success(`Reporte Relevante generado: ${data.reporte.archivo_nombre} (${data.reporte.num_operaciones} op.)`);
      _capturarAvisoOrgano(data);
      setValidacion(null);
      await loadAll();
    } catch (e) { _manejarError(e); } finally { setBusy(false); }
  };

  const generarDesdeCaso = async (casoId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pld/reportes/desde-caso/${casoId}`);
      toast.success(`Reporte generado: ${data.reporte.archivo_nombre}`);
      _capturarAvisoOrgano(data);
      await loadAll();
    } catch (e) { _manejarError(e); } finally { setBusy(false); }
  };

  const verPreview = async (rep) => {
    try {
      const { data } = await api.get(`/pld/reportes/${rep.id}/preview`);
      setPrev(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const guardarAcuse = async () => {
    setBusy(true);
    try {
      await api.post(`/pld/reportes/${acuseFor.id}/acuse`, acuseForm);
      toast.success("Acuse / folio SITI registrado.");
      setAcuseFor(null);
      await loadAll();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const transmitir = async (rep) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pld/reportes/${rep.id}/transmitir`);
      toast.info(data.mensaje || "Reporte listo para transmisión manual al SITI.");
      await loadAll();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const importarCatalogos = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("¿Importar y fusionar los códigos del archivo con la tabla de catálogos actual?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/exportar/pld/catalogos/importar`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "No se pudo importar.");
      toast.success(`${data.mapeos_importados} mapeo(s) importado(s).`);
      await loadCat();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!canView) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="reportes-uif-restringido">
        <ShieldAlert className="h-5 w-5" /> Área restringida: solo el Oficial de Cumplimiento, el administrador de la entidad o el analista PLD pueden operar los reportes UIF (segregación de funciones).
      </div>
    );
  }

  const faltCount = (validacion?.faltantes_catalogo?.length || 0) + (validacion?.faltantes_config?.length || 0);

  return (
    <div className="space-y-5" data-testid="reportes-uif">
      {/* Feb-2026 · Iter-107: banner persistente cuando el último reporte se generó con
          órgano supervisor en marcador (XXXXXX). NO desaparece solo — el OC debe cerrarlo. */}
      {avisoOrgano && (
        <div data-testid="aviso-organo-marcador-banner"
          className="flex flex-wrap items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 p-4 shadow-sm">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-700" />
          </div>
          <div className="min-w-[280px] flex-1 text-sm">
            <p className="font-heading font-semibold text-red-900">
              Este archivo NO puede subirse al SITI todavía — es un BORRADOR DE REVISIÓN.
            </p>
            <p className="mt-1 text-red-800">
              El órgano supervisor (columna 4 del layout) todavía trae el marcador de seis equis
              (<span className="rounded bg-white/70 px-1.5 py-0.5 font-mono text-red-900">{avisoOrgano.valor_actual || "vacío"}</span>).
              La regla <b>VRC4R2</b> exige exactamente 6 caracteres numéricos, y el SITI rechazará
              el archivo así. Este reporte se emitió como <b>borrador</b> para que puedas revisar
              su contenido, pero antes de transmitirlo un super_admin debe capturar la clave
              CNBV definitiva en el panel global de PLD. Contacta a soporte Felvora si no
              tienes esa clave todavía.
            </p>
            {avisoOrgano.archivo_nombre && (
              <p className="mt-2 text-xs font-mono text-red-700">
                Archivo generado: {avisoOrgano.archivo_nombre}
              </p>
            )}
          </div>
          <Button data-testid="aviso-organo-cerrar-btn" size="sm" variant="outline"
            className="border-red-400 text-red-700 hover:bg-red-100"
            onClick={() => setAvisoOrgano(null)}>
            Entendido, cerrar
          </Button>
        </div>
      )}
      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4 text-xs text-sky-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Generador de reportes para el <strong>SITI-PLD (UIF/CNBV)</strong>. Layout oficial DOF 30-MAY-2016 (36 columnas, TXT). El archivo exporta los <strong>códigos oficiales</strong> de catálogo, no los nombres. # REVISIÓN HUMANA: la transmisión real la realiza el OC con su Cuenta Única tras validar el archivo. El acuse MOCK no sustituye al acuse oficial.
        </p>
      </div>

      {/* Catálogos + claves */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="catalogos-card">
        <div className="flex items-start gap-3 text-sm">
          <Settings2 className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <p className="font-heading font-semibold text-slate-800">Catálogos y claves SITI</p>
            <p className="text-slate-500">
              Clave sujeto obligado: <strong>{cat.editables?.clave_sujeto_obligado || "— sin definir —"}</strong>
              {" · "}Localidad de sucursal: {cat.editables?.localidad_default || "—"}
              {" · "}C.P.: {cat.editables?.cp_sucursal_default || "—"}
              {" · "}Moneda default: {cat.editables?.moneda_default || "—"}
            </p>
            <p className="text-xs text-slate-400">Las claves de columnas 26 (localidad) y 28 (actividad económica) se resuelven desde el expediente oficial del cliente contra el catálogo CNBV RIPS F36.</p>
          </div>
        </div>
        {canEditCat ? (
          <Button data-testid="editar-catalogos-btn" size="sm" variant="outline" onClick={() => setCatOpen(true)}>
            <Settings2 className="mr-1.5 h-4 w-4" /> Editar catálogos
          </Button>
        ) : (
          <Badge variant="outline" className="text-xs text-slate-500">Solo el Oficial de Cumplimiento edita</Badge>
        )}
      </div>

      {/* Generar Reporte Relevante */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="generar-relevante-card">
        <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <FileText className="h-4 w-4 text-accent" /> Reporte de Operaciones Relevantes (28ª)
        </h4>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs">Año</Label>
            <Input data-testid="rel-anio" className="w-28" value={anio} onChange={(e) => setAnio(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs">Mes</Label>
            <Select value={mes} onValueChange={setMes}>
              <SelectTrigger data-testid="rel-mes" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="validar-btn" variant="outline" disabled={busy} onClick={() => validar()}>
            <ListChecks className="mr-1.5 h-4 w-4" /> Validar catálogos
          </Button>
          <TooltipButton tooltip="Generar el reporte oficial en formato SITI-PLD"
            data-testid="generar-relevante-btn" disabled={busy || !canGenerar} onClick={generarRelevante} className="bg-primary hover:bg-sidebar-hover">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Generar reporte TXT
          </TooltipButton>
        </div>

        {/* Resultado de validación */}
        {validacion && (
          <div className="mt-4" data-testid="validacion-resultado">
            {validacion.listo ? (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-sm text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Catálogos completos ({validacion.total_registros} registro(s) en el periodo). Listo para generar.
              </div>
            ) : (
              <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> Faltan {faltCount} código(s) de catálogo antes de generar.</p>
                {validacion.faltantes_config?.length > 0 && (
                  <p className="text-xs text-amber-700">Configuración faltante: {validacion.faltantes_config.map((f) => f.etiqueta).join(", ")}. Edítala en “Editar catálogos”.</p>
                )}
                {validacion.faltantes_catalogo?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-700">
                      Los siguientes valores no tienen clave oficial CNBV resuelta en el expediente del cliente.
                      Corrige el expediente (Localidad / Actividad económica) desde el módulo <strong>Clientes</strong>;
                      la clave se toma del catálogo oficial CNBV (RIPS F36) — ya no se capturan mapeos manuales aquí.
                    </p>
                    <div className="space-y-1">
                      {validacion.faltantes_catalogo.map((f) => {
                        const key = `${f.catalogo}|${f.valor}`;
                        return (
                          <div key={key} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`falt-row-${f.valor}`}>
                            <Badge variant="outline" className="text-[10px]">{f.etiqueta}</Badge>
                            <span className="font-medium text-slate-700">{f.valor}</span>
                            <span className="text-slate-400">({f.registros_afectados} cliente(s) afectado(s))</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Casos listos para reporte (Inusual / desde dictamen) */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="casos-listos-card">
        <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <FileText className="h-4 w-4 text-primary" /> Casos dictaminados “SE REPORTA” (29ª/30ª/33ª/34ª)
        </h4>
        <DataTable testid="table-casos-listos-uif" rows={casosListos}
          columns={[
            { key: "folio", label: "Folio" },
            { key: "cliente", label: "Cliente" },
            { key: "regla", label: "Origen" },
            { key: "dictamen", label: "Tipo reporte", render: (r) => r.dictamen?.tipo_reporte || "—" },
          ]}
          actions={(row) => (
            <TooltipButton tooltip="Genera el reporte oficial SITI-PLD a partir de este caso"
              data-testid={`gen-caso-${row.id}`} size="sm" variant="outline" disabled={busy || !canGenerar} onClick={() => generarDesdeCaso(row.id)}>
              <FileText className="mr-1.5 h-4 w-4" /> Generar TXT
            </TooltipButton>
          )}
          emptyLabel="Sin casos listos para reportar." />
      </div>

      {/* Histórico de reportes */}
      <div data-testid="historico-reportes-card">
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <FileArchive className="h-4 w-4 text-slate-500" /> Histórico de reportes SITI-PLD
        </h4>
        <DataTable testid="table-reportes-uif" rows={reportes}
          columns={[
            { key: "archivo_nombre", label: "Archivo",
              render: (r) => (
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs">{r.archivo_nombre}</span>
                  {esBorradorNoEnviable(r) && (
                    <Badge data-testid={`borrador-no-enviable-${r.id}`}
                      className="border border-red-300 bg-red-100 text-red-800 hover:bg-red-100">
                      <AlertTriangle className="mr-1 h-3 w-3" /> Borrador no enviable
                    </Badge>
                  )}
                  {Number(r.num_operaciones) === 0 && (
                    <Badge data-testid={`sin-operaciones-${r.id}`}
                      className="border border-sky-300 bg-sky-100 text-sky-800 hover:bg-sky-100"
                      title="Reporte sin operaciones — Art. 4: archivo con columnas 1, 2, 4 y 5 únicamente.">
                      Sin operaciones
                    </Badge>
                  )}
                </span>
              ),
            },
            { key: "tipo", label: "Tipo" },
            { key: "periodo", label: "Periodo" },
            { key: "generado_en", label: "Generado",
              render: (r) => (
                <span className="flex flex-col gap-0.5" data-testid={`gen-${r.id}`}>
                  <span className="text-xs text-slate-700">{fmtFechaHora(r.generado_en)}</span>
                  {r.generado_por && (
                    <span className="text-[11px] text-slate-500" data-testid={`gen-autor-${r.id}`}>por {r.generado_por}</span>
                  )}
                </span>
              ),
            },
            { key: "fecha_limite_calc", label: "Fecha límite (DOF)", render: renderPlazo },
            { key: "num_operaciones", label: "Ops",
              render: (r) => (
                <span data-testid={`ops-${r.id}`} className={Number(r.num_operaciones) === 0 ? "font-mono text-sky-700" : "font-mono text-slate-700"}>
                  {Number(r.num_operaciones) || 0}
                </span>
              ),
            },
            { key: "estatus", label: "Estatus", kind: "badge" },
            { key: "acuse", label: "Acuse", render: (r) => r.acuse || "—" },
          ]}
          actions={(row) => (
            <div className="flex items-center justify-end gap-1.5">
              <TooltipButton tooltip="Ver el contenido del reporte antes de transmitirlo"
                data-testid={`prev-${row.id}`} size="icon" variant="ghost" onClick={() => verPreview(row)}><Eye className="h-4 w-4" /></TooltipButton>
              <TooltipButton tooltip="Descargar el TXT oficial para subir al portal SITI de la CNBV"
                data-testid={`dl-txt-${row.id}`} size="icon" variant="ghost" onClick={() => downloadBlob(`/pld/reportes/${row.id}/descargar`, row.archivo_nombre || `${row.id}.txt`).catch((e) => toast.error(e.message))}><Download className="h-4 w-4" /></TooltipButton>
              <TooltipButton tooltip="Descargar el reporte en Excel (solo para revisión interna)"
                data-testid={`dl-xlsx-${row.id}`} size="icon" variant="ghost" onClick={() => downloadBlob(`/exportar/pld/reporte-uif/${row.id}`, `reporte_uif_${row.archivo_nombre || row.id}.xlsx`).catch((e) => toast.error(e.message))}><FileSpreadsheet className="h-4 w-4" /></TooltipButton>
              {row.evidencia_key && (
                <TooltipButton tooltip="Descargar el paquete de evidencia (documentos que sustentan el reporte)"
                  data-testid={`dl-zip-${row.id}`} size="icon" variant="ghost" onClick={() => downloadBlob(`/pld/reportes/${row.id}/evidencia`, `evidencia_${row.id}.zip`).catch((e) => toast.error(e.message))}><FileArchive className="h-4 w-4" /></TooltipButton>
              )}
              {canAcuse && (
                <>
                  <TooltipButton
                    tooltip={esBorradorNoEnviable(row)
                      ? motivoBorrador(row)
                      : "Marcar que el Oficial de Cumplimiento ya subió el reporte al portal SITI"}
                    data-testid={`tx-${row.id}`} size="icon" variant="ghost"
                    disabled={busy || esBorradorNoEnviable(row)}
                    onClick={() => transmitir(row)}><Send className="h-4 w-4" /></TooltipButton>
                  <TooltipButton tooltip="Registrar el folio de acuse que devolvió la CNBV"
                    data-testid={`acuse-${row.id}`} size="icon" variant="ghost" onClick={() => { setAcuseFor(row); setAcuseForm({ acuse: row.acuse || "", folio_siti: row.folio_siti || "", fecha_envio: row.fecha_envio || "" }); }}><CheckCircle2 className="h-4 w-4" /></TooltipButton>
                </>
              )}
            </div>
          )}
          emptyLabel="Aún no se han generado reportes." />
      </div>

      {/* Dialog: editar catálogos */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="catalogos-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catálogos y claves SITI-PLD</DialogTitle>
            <DialogDescription>Solo el Oficial de Cumplimiento. Configura los cuatro campos de identidad del sujeto obligado que se emiten en cada archivo SITI (RIPS F36). Las claves de localidad y actividad económica de los clientes se resuelven desde su expediente contra el catálogo oficial CNBV.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
            <ExportExcelButton endpoint="/exportar/pld/catalogos/xlsx" filename="catalogos_pld.xlsx"
              label="Exportar catálogos" testid="export-catalogos-btn" />
            <Button data-testid="importar-catalogos-btn" size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById("imp-cat-file").click()}>
              <Upload className="mr-1.5 h-4 w-4" /> Importar
            </Button>
            <input id="imp-cat-file" data-testid="imp-cat-file" type="file" accept=".xlsx" className="hidden" onChange={importarCatalogos} />
            <span className="self-center text-xs text-slate-400">Edita el código fuera y reimporta (se fusiona con lo existente).</span>
          </div>
          <div className="space-y-4">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="cat-organo-supervisor-fijo">
              <p><b>Órgano supervisor (columna 4 del layout SITI):</b> fijo por código para SOFOM ENR. Ver <em>REPORT_CONFIG.clave_organo_supervisor</em> en el backend. Este campo NO es editable por el Oficial de Cumplimiento — el órgano supervisor de una SOFOM ENR es siempre la CNBV.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CAMPOS_CLAVE.map((c) => (
                <div key={c.k}>
                  <Label className="mb-1 block text-xs">{c.label}</Label>
                  <Input data-testid={`cat-${c.k}`} value={catForm[c.k] || ""} onChange={(e) => setCatForm((p) => ({ ...p, [c.k]: e.target.value }))} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancelar</Button>
            <Button data-testid="guardar-catalogos-btn" disabled={busy} onClick={guardarCatalogos} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: preview TXT */}
      <Dialog open={!!prev} onOpenChange={(v) => !v && setPrev(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{prev?.archivo_nombre}</DialogTitle>
            <DialogDescription>Vista previa del archivo TXT ({prev?.num_columnas} columnas) · SHA-256: {prev?.hash_sha256?.slice(0, 16)}…</DialogDescription>
          </DialogHeader>
          <pre className="overflow-x-auto rounded-md bg-slate-900 p-3 text-xs text-emerald-200" data-testid="preview-contenido">{prev?.contenido}</pre>
        </DialogContent>
      </Dialog>

      {/* Dialog: acuse */}
      <Dialog open={!!acuseFor} onOpenChange={(v) => !v && setAcuseFor(null)}>
        <DialogContent data-testid="acuse-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar acuse SITI — {acuseFor?.archivo_nombre}</DialogTitle>
            <DialogDescription>Captura el acuse y folio oficial tras la transmisión real (Cuenta Única).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="mb-1 block text-xs">Acuse</Label><Input data-testid="acuse-acuse" value={acuseForm.acuse} onChange={(e) => setAcuseForm((p) => ({ ...p, acuse: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs">Folio SITI</Label><Input data-testid="acuse-folio" value={acuseForm.folio_siti} onChange={(e) => setAcuseForm((p) => ({ ...p, folio_siti: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs">Fecha de envío</Label><Input data-testid="acuse-fecha" type="date" value={acuseForm.fecha_envio} onChange={(e) => setAcuseForm((p) => ({ ...p, fecha_envio: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcuseFor(null)}>Cancelar</Button>
            <Button data-testid="guardar-acuse-btn" disabled={busy} onClick={guardarAcuse} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar acuse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tarea 6 (jul-2026): diálogo bloqueante con lista literal de datos faltantes
          por configuración y por cliente. Se abre cuando `_manejarError` detecta un
          HTTP 400 con payload `{faltantes_config, faltantes_clientes}`. */}
      <Dialog open={!!bloqueo} onOpenChange={(open) => !open && setBloqueo(null)}>
        <DialogContent data-testid="bloqueo-datos-faltantes" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> No se puede generar el reporte
            </DialogTitle>
            <DialogDescription>
              Corrige los datos que faltan y vuelve a intentar. El sistema no genera archivos con
              información incompleta para no rechazar en el SITI-PLD.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {bloqueo?.faltantes_config?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                   data-testid="bloqueo-faltantes-config">
                <p className="mb-1 font-semibold text-amber-900">Configuración de la entidad</p>
                <ul className="ml-4 list-disc text-amber-900">
                  {bloqueo.faltantes_config.map((c, i) => (<li key={i}>{c}</li>))}
                </ul>
                <p className="mt-2 text-xs text-amber-700">
                  Estos valores se capturan una sola vez en <strong>Catálogos SITI (RIPS F36)</strong> — abre el
                  panel de catálogos desde el botón “Configurar catálogos”.
                </p>
                {bloqueo.faltantes_config.some((c) => (c || "").toLowerCase().includes("clave del sujeto")) && (
                  <p className="mt-2 rounded border border-amber-300 bg-white/60 p-2 text-xs text-amber-900" data-testid="aviso-clave-csfim">
                    <b>¿No la tienes todavía?</b> La <b>Clave del sujeto obligado (CASFIM)</b> la asigna la <b>CNBV</b>
                    al alta de la entidad como sujeto obligado, y suele tardar semanas. <b>No detiene tu operación</b>:
                    puedes seguir dando de alta clientes, originar créditos, registrar pagos, generar alertas y dictaminar
                    casos. Solo bloquea la generación del archivo del SITI. Captúrala aquí en cuanto la CNBV te la
                    comunique (Catálogos SITI → <em>Clave sujeto obligado (CASFIM)</em>).
                  </p>
                )}
              </div>
            )}
            {bloqueo?.faltantes_clientes?.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3"
                   data-testid="bloqueo-faltantes-clientes">
                <p className="mb-1 font-semibold text-red-900">Expediente de clientes involucrados</p>
                <ul className="ml-4 list-disc text-red-900">
                  {bloqueo.faltantes_clientes.map((c) => (
                    <li key={c.cliente_id} data-testid={`bloqueo-cliente-${c.cliente_id}`}>
                      <span className="font-medium">{c.nombre}</span>: {(c.faltantes || []).join(", ")}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-red-700">
                  Edita cada cliente desde <strong>Clientes → Expediente</strong> y completa los campos
                  faltantes (domicilio, colonia, localidad oficial CNBV, actividad económica).
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBloqueo(null)} data-testid="bloqueo-cerrar-btn">Entendido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// El componente `MapEditor` fue eliminado en feb-2026 (Msg 245). La captura de mapeos
// manuales de ciudades/actividades/monedas ya no ocurre en la UI — las claves oficiales
// CNBV se resuelven desde el expediente del cliente (columnas 26 y 28). La lógica de
// respaldo en el generador (`pld_reportes.py::_datos_persona::_code(maps, ...)`) se
// conservó por si una entidad legítima llega a requerirla en el futuro.
